import { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from 'react';
import './ChatPage.scss';
import { ThemeContext } from './contexts/themeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faArrowCircleUp, faSignOutAlt, faSignIn, faDotCircle } from '@fortawesome/free-solid-svg-icons';
import { TypeAnimation } from 'react-type-animation';
import ReactMarkdown from 'react-markdown';
import LoadingBubble from './components/LoadingBubble';
import axios from "axios";
import useLogout from "./common/UseLogout";
import { useNavigate } from 'react-router-dom';
const { v4: uuidv4 } = require('uuid');

const ChatPage = forwardRef(({refreshNav, isGuest}, ref) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const textAreaRef = useRef(null);
  const [promptValue, setPromptValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentConverstation, setCurrentConversation] = useState([]);
  const [data, setData] = useState({});
  const messagesEndRef = useRef(null);
  const [scrollInterval, setScrollInterval] = useState(null);
  const [uuid, setuuid] = useState('');
  const logout = useLogout();
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    startNewChat,
    loadPreviousChat
  }));

  const startNewChat = () => {
    clearSession();
  }

  const loadPreviousChat = async (item) => {
    try {
      setIsLoading(true);
      setCurrentConversation([])
      await removeOldSession();
      if (item?.uuid) {
        setuuid(item.uuid);
        localStorage.setItem('stadata-session-type', item.uuid);
        initiate(true);
      }
      setIsLoading(false);
    } catch(err) {
      setIsLoading(false);
      console.log('err', err);
    }
  }

  const useAutosizeTextArea = (textAreaRef, value) => {
    useEffect(() => {
      if (textAreaRef) {
        textAreaRef.style.height = "0px";
        const scrollHeight = textAreaRef.scrollHeight;
        textAreaRef.style.height = scrollHeight + "px";
      }
    }, [textAreaRef, value]);
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentConverstation]);

  useEffect(() => {
    scrollToBottom();
  }, [scrollInterval]);

  useEffect(() => {
    initiate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiate = async (loadingPrevious = false) => {
    try {
      let sessionkey = localStorage.getItem('stadata-session-type');
      if (!sessionkey && !loadingPrevious) {
        initiateFreshStart();
      } else {
        setuuid(sessionkey);
        const apiUrl = `${process.env.REACT_APP_API_URL}/continue-previous-chat/${sessionkey}`;
        const token = localStorage.getItem('stadata-token');
        const headers = { 'x-access-token': token };
        const response = await axios.get(apiUrl, { headers });
        if (response?.status === 200 && response?.data?.success && response?.data?.data?.messages?.length > 0) {
          if (response.data.data.messages[0]?.role === 'system') {
            setCurrentConversation(response.data.data.messages.slice(1));
          } else {
            setCurrentConversation(response.data.data.messages);
          }
          setData(response.data.data);
          setTimeout(() => {
            scrollToBottom();
            console.log('data', data);
          }, 300);
        } else {
          initiateFreshStart();
        }
      }
      setIsLoading(false);
    } catch(err) {
      console.log('err initiating chat', err);
      setIsLoading(false);
      if (err?.response?.status === 401) {
        // return logout();
      }
    }
  }

  const removeOldSession = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/clear-session/${uuid}`;
      const token =  localStorage.getItem("stadata-token");
      const headers = {
        "x-access-token": token
      };
      localStorage.removeItem("stadata-session-type");
      await axios.get(apiUrl, { headers });
    } catch(err) {
      console.log('err clearSession', err);
    }
  }

  const clearSession = async () => {
    try {
      setIsLoading(true);
      const apiUrl = `${process.env.REACT_APP_API_URL}/clear-session/${uuid}`;
      const token = localStorage.getItem("stadata-token");
      const headers = {
        "x-access-token": token
      };
      localStorage.removeItem("stadata-session-type");
      await axios.get(apiUrl, { headers });
      setPromptValue([]);
      setCurrentConversation('');
      setData({});
      initiateFreshStart();
      setIsLoading(false);
    } catch(err) {
      console.log('err clearSession', err);
    }
  }

  const initiateFreshStart = () => {
    setTimeout(() => {
      let sessionkey = localStorage.getItem("stadata-session-type");
      if (!sessionkey) {
        sessionkey = uuidv4();
        localStorage.setItem('stadata-session-type', sessionkey);
      }
      setuuid(sessionkey);
      setCurrentConversation([]);
      setData({});
      setIsLoading(false);
    }, 1000);
  }

  const scrollStarted = (end) => {
    if (end && scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    } else if (!end) {
      let s = setInterval(() => {
        scrollToBottom();
      }, 250);
      setScrollInterval(s);
    }
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  useAutosizeTextArea(textAreaRef.current, promptValue);

  const handleChange = (evt) => {
    const val = evt.target?.value;
    setPromptValue(val);
  };

  const pullNavElements = () => {
    if (currentConverstation.length <= 2) {
      refreshNav();
    }
  }

  const sendMessage = async (e) => {
    try {
      e.preventDefault();
      if (!promptValue) return;
      if (isLoading) return;
      setIsLoading(true);
      let apiUrl = `${process.env.REACT_APP_API_URL}/chat-qwen`;
      if (!isGuest || isGuest === 'false') {
        apiUrl = apiUrl + '-customer';
      }
      const token = localStorage.getItem("stadata-token");
      const headers = {
        "x-access-token": token
      };
      let msg = promptValue;
      let list = JSON.parse(JSON.stringify(currentConverstation));
      setPromptValue('');
      setCurrentConversation('');
      const payload = {
        textPrompt: msg,
        uuid: uuid
      }
      list.push({ role: 'user', text: msg });
      setCurrentConversation(list);
      const response = await axios.post(apiUrl, payload, { headers });
      if (response.status === 200) {
        list.push({ role: 'assistant', text: response?.data?.resp });
        setCurrentConversation(list);
        setPromptValue('');
        scrollStarted(false);
        setIsLoading(false);
      }
    } catch(err) {
      setIsLoading(false);
      console.log('err sendMessage', err);
      if (err?.response?.status === 401) {
        // return logout();
      }
    }
  }

  const performLogout = () => {
    try {
      clearSession()
      logout();
    } catch(err) {
      console.log('err performLogout', err);
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      sendMessage(e);
    }
  };

  const guestLogin = () => {
    clearSession();
    localStorage.removeItem('stadata-auth');
    localStorage.removeItem('stadata-token');
    localStorage.setItem("stadata-guest-session", true);
    navigate('/login');
  }

  return (
    <>
      <div className="main-frame flex flex-col items-center h-screen">
      <div class="flex flex-col md:flex-row md:justify-between w-full">
        <div className="w-full px-7 py-5 flex items-center justify-start">
        {( isGuest === true || isGuest === 'true') ? (
          <label className="pr-text text-sm">
            <FontAwesomeIcon icon={faDotCircle} color="grey"/>&nbsp; Limited Access
          </label>
        ) : (
          <label className="pr-text text-sm"> <FontAwesomeIcon icon={faDotCircle} color="green"/>&nbsp; Full Access</label>
        )}
        </div>
        <div className="icons-right w-full px-7 py-5 flex items-center justify-end">
          {(isGuest === true || isGuest === 'true') ? (
            <button title="Log in to your stadata account" className="mr-8 button" style={{fontSize: "1.1rem"}} onClick={() => guestLogin()}>
            Sign In <FontAwesomeIcon icon={faSignIn} />
            </button>
          ) : (
            <button title="Sign out of stadata" className="mr-8 button" style={{fontSize: "1.1rem"}} onClick={() => performLogout()}>
              Logout <FontAwesomeIcon icon={faSignOutAlt} />
            </button>
          )}
          <button title="Switch theme to dark/light" className="button" style={{fontSize: "1.1rem"}} onClick={toggleTheme}>
            { theme === 'light' ? <FontAwesomeIcon icon={faMoon} /> : <FontAwesomeIcon icon={faSun} /> }
          </button>
        </div>
      </div>
        
        <div className="container main-container">
          {currentConverstation?.length === 0 ?
            (
              <>
                <div className="w-full"></div>
                <div className="w-full">
                  <div className="w-1/4 m-auto rounded-md pr-border p-4">
                    <div className="flex-1 flex w-full justify-between">
                      <h1>Hello 👋</h1>
                    </div>
                      <p className="pr-text text-xs mt-1">Start chatting with the stadata Ai assistant..</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full flex flex-col h-96 flex-grow justify-start overflow-y-auto hide-scrollbar scroll-smooth" ref={messagesEndRef}>
                { currentConverstation.map((item, index) => {
                  return (
                    <div className="w-full" key={index}>
                      <div className="w-1/2 m-auto rounded-md p-2">
                        { item.role === 'user' ? (
                          <div className="flex justify-end">
                            <div className="user-message-bubble">
                              <span className="pr-text text-sm mt-1 right">
                                <ReactMarkdown className="mdown" linkTarget="_blank" children={item?.text  || item?.content} />
                              </span>
                            </div>
                          </div>
                          ) : (
                            <div className="flex flex-row justify-start">
                              <div className="chat-message-icon mr-2 ">
                                <img src="stadata-logo.png" width="26px" alt="logoalt"></img>
                              </div>
                              <div className="chat-message-bubble w-full">
                                <>
                                  { (scrollInterval && index === currentConverstation.length - 1) || (currentConverstation.length === 1 && index === currentConverstation.length - 1) ? (
                                    <TypeAnimation
                                        sequence={[
                                          item.text,
                                          () => {
                                            scrollStarted(true);
                                            pullNavElements();
                                          },
                                        ]} wrapper="span" speed={95} cursor={false}
                                        style={{  display: 'inline' }} repeat={0}
                                      />
                                    ) : (
                                      <ReactMarkdown className="mdown" linkTarget="_blank" children={item?.text  || item?.content} />
                                  )}
                                </>
                              </div>
                            </div>
                          )
                        }
                      </div>
                    </div>
                  )
                }) }
                { isLoading && (
                  <div className="w-1/2 mx-auto rounded-md p-2">
                    <LoadingBubble />
                  </div>
                )}
              </div>
            )
          }  
          <div className="mb-2 py-4 mt-6">
            <form className="flex items-center justify-center w-full relative" onSubmit={sendMessage}>
              <div className="chat-form-div w-1/2">
                <textarea
                  onChange={handleChange}
                  ref={textAreaRef}
                  value={promptValue} 
                  rows="1"
                  disabled={isLoading}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="chat-text-area pr-border p-3 shadow-sm focus:ring-0 focus:outline-none transition duration-150 ease-in-out pr-12"
                >
                </textarea>
                <button disabled={isLoading} type="submit" className="submit-key"><FontAwesomeIcon size="2xl" icon={faArrowCircleUp} /></button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
});

export default ChatPage;
